// ============================================================
// Masraf (gider) yönetimi (§11)
//
// Sistem parayı tek yönde tanıyordu: veliden gelen tahsilat.
// Kurumun en büyük gider kalemi (personel) ve işletme masrafı
// hiçbir yerde kayıtlı değildi, bu yüzden şubenin kâr edip
// etmediği söylenemiyordu.
// ============================================================

import { DONEM } from "../config.js";
import { GIDER_KATEGORI } from "../data/tanimlar.js";
import { B, subeBaglaminda, subeBulHerYerde, subeler } from "./context.js";
import { acikFaturalar, faturaTutari, tahsilEdilen } from "./invoices.js";
import { bordroUret } from "./payroll.js";

export { GIDER_KATEGORI };


const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

// ---------- Giderler ----------

export const giderler = branchId => sube(branchId).expenses ?? [];

export const donemGiderleri = (branchId, period = DONEM) =>
  giderler(branchId).filter(g => g.period === period);

export const onayBekleyenler = branchId =>
  giderler(branchId).filter(g => g.status === "onay_bekliyor" || g.status === "planlandi");

/** Vadesi yaklaşan tekrarlayan giderler. */
export const tekrarlayanlar = branchId =>
  giderler(branchId).filter(g => g.recurring?.nextAt);

export const giderToplami = list => list.reduce((a, g) => a + g.amount, 0);

/**
 * Kategori bazlı dönem toplamı. Personel kalemi bordrodan gelir —
 * elle girilmiş maaş kaydı yoksa bile gerçek maliyet görünsün.
 */
export function kategoriToplamlari(branchId, period = DONEM) {
  const kayitlar = donemGiderleri(branchId, period);
  const toplam = {};

  for (const k of Object.keys(GIDER_KATEGORI)) toplam[k] = 0;
  for (const g of kayitlar) toplam[g.category] = (toplam[g.category] ?? 0) + g.amount;

  // Onaylanmış bordro gider defterine otomatik akar — çift kayıt olmasın
  // diye elle girilmiş personel gideri varsa üzerine eklenmez, büyüğü alınır.
  const bordro = bordroUret(branchId, period);
  toplam.personel = Math.max(toplam.personel, bordro.totalEmployerCost);

  return toplam;
}

export const donemGiderToplami = (branchId, period = DONEM) =>
  Object.values(kategoriToplamlari(branchId, period)).reduce((a, b) => a + b, 0);

// ---------- Gelir ----------

/** Dönemin tahakkuk eden ve tahsil edilen geliri. */
export function donemGeliri(branchId, period = DONEM) {
  const t = sube(branchId);
  return subeBaglaminda(t.id, () => {
    const faturalar = t.payments.filter(p => p.period === period);
    const tahakkuk = faturalar.reduce((a, p) => {
      const s = t.students.find(x => x.id === p.studentId);
      return a + (s ? faturaTutari(s, p) : 0);
    }, 0);
    const tahsil = faturalar.reduce((a, p) => a + tahsilEdilen(p), 0);

    const acik = acikFaturalar(t).reduce((a, p) => {
      const s = t.students.find(x => x.id === p.studentId);
      return a + (s ? faturaTutari(s, p) - tahsilEdilen(p) : 0);
    }, 0);

    return {
      tahakkuk, tahsil, acik,
      tahsilatOrani: tahakkuk ? Math.round((tahsil / tahakkuk) * 100) : null,
    };
  });
}

// ---------- Gelir–gider ----------

/** Şubenin dönem sonucu. Bu sayı olmadan kârlılık konuşulamaz. */
export function donemSonucu(branchId, period = DONEM) {
  const t = sube(branchId);
  const gelir = donemGeliri(t.id, period);
  const kategoriler = kategoriToplamlari(t.id, period);
  const gider = Object.values(kategoriler).reduce((a, b) => a + b, 0);
  const ogrenci = t.students.length;

  return {
    branchId: t.id,
    branchName: t.name,
    period,
    gelir,
    gider,
    kategoriler,
    net: gelir.tahakkuk - gider,
    nakitNet: gelir.tahsil - gider,
    ogrenciBasinaGelir: ogrenci ? Math.round(gelir.tahakkuk / ogrenci) : 0,
    ogrenciBasinaMaliyet: ogrenci ? Math.round(gider / ogrenci) : 0,
    marj: gelir.tahakkuk ? Math.round(((gelir.tahakkuk - gider) / gelir.tahakkuk) * 100) : null,
  };
}

/** Tüm şubelerin karşılaştırması — hangisi kârlı, hangisi değil. */
export const subeKarsilastirma = (period = DONEM) =>
  subeler().map(t => donemSonucu(t.id, period)).sort((a, b) => b.net - a.net);

// ---------- Bütçe ----------

export function butceKarsilastirma(branchId, period = DONEM) {
  const t = sube(branchId);
  const butce = t.budgets?.[period];
  if (!butce) return null;

  const gercek = kategoriToplamlari(t.id, period);
  return Object.entries(GIDER_KATEGORI).map(([kod, tanim]) => {
    const hedef = butce[kod] ?? 0;
    const olan = gercek[kod] ?? 0;
    return {
      kod,
      label: tanim.label,
      hedef,
      olan,
      sapma: olan - hedef,
      oran: hedef ? Math.round((olan / hedef) * 100) : null,
      asim: hedef > 0 && olan > hedef,
    };
  }).sort((a, b) => b.olan - a.olan);
}

// ---------- Kasa ----------

export function kasaDefteri(branchId, period = DONEM) {
  const kasa = sube(branchId).cashbox?.[period];
  if (!kasa) return null;

  const giris = kasa.movements.filter(m => m.direction === "giris").reduce((a, m) => a + m.amount, 0);
  const cikis = kasa.movements.filter(m => m.direction === "cikis").reduce((a, m) => a + m.amount, 0);

  return {
    ...kasa,
    period,
    giris,
    cikis,
    bakiye: kasa.openingBalance + giris - cikis,
  };
}
